"use client";
import React, { useId, useRef, useEffect } from "react";
import { motion } from "motion/react";
import Icons from "@/lib/icons";
import BorderWrapper from "@/components/ui/border-wrapper";
import AnimatedCard from "@/components/ui/animated-card";
import Image from "next/image";
import { useGSAP } from "@/components/providers/gsap-provider";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "@/hooks/use-media-query";

const containerVariants = {
  hidden: { opacity: 1 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const Features = () => {
  const t = useTranslations("features");
  const svgId = useId().replace(/:/g, "");
  const glowId = `${svgId}-glow`;
  const linearGradientId = `${svgId}-linear-gradient`;
  const linearGradient2Id = `${svgId}-linear-gradient-2`;
  const beamGlowId = `${svgId}-beam-glow`;
  const { gsap, registerAnimation } = useGSAP();
  const mouseShadowRef = useRef(null);
  const svgRef = useRef(null);
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [uiHovered, setUiHovered] = React.useState(false);
  const [cloudHovered, setCloudHovered] = React.useState(false);
  const [webHovered, setWebHovered] = React.useState(false);

  // Constant, infinite pulse between 0.9 and 1 (no opacity/y changes)
  const shapeVariants = {
    visible: {
      scale: [0.9, 1, 0.9] as number[],
      transition: { duration: 4, ease: "easeInOut", repeat: Infinity },
    },
  } as const;

  // Mouse shadow animation effect with interactive border effects
  useEffect(() => {
    if (svgRef.current && mouseShadowRef.current) {
      const svg = svgRef.current as SVGSVGElement;
      const mouseShadowElement = mouseShadowRef.current;

      // Create the animation
      const mouseAnimation = gsap.timeline();

      // Get interactive elements - IMPORTANT: querySelector returns a live NodeList
      const interactiveRects = svg.querySelectorAll("rect.cls-3, rect.cls-4");

      // Create a map to store the state for each interactive rectangle
      const rectStates = new Map();

      // Track mouse movement — throttled via rAF for Safari performance
      let rafId: number | null = null;
      const handleMouseMove = (e: MouseEvent) => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          handleMouseMoveInner(e);
        });
      };
      const handleMouseMoveInner = (e: MouseEvent) => {
        const svgRect = svg.getBoundingClientRect();
        // Get mouse position relative to SVG
        const mouseX = e.clientX - svgRect.left;
        const mouseY = e.clientY - svgRect.top;

        // Check if mouse is over any interactive rect
        let hoveredRect: Element | null = null;
        interactiveRects.forEach((rect) => {
          const rectEl = rect as SVGRectElement;
          const x = parseFloat(rectEl.getAttribute("x") || "0");
          const y = parseFloat(rectEl.getAttribute("y") || "0");
          const width = parseFloat(rectEl.getAttribute("width") || "0");
          const height = parseFloat(rectEl.getAttribute("height") || "0");

          if (
            mouseX >= x &&
            mouseX <= x + width &&
            mouseY >= y &&
            mouseY <= y + height
          ) {
            hoveredRect = rect;
          }
        });

        // Immediately update all elements based on hover state
        interactiveRects.forEach((rect) => {
          const state = rectStates.get(rect);
          if (state) {
            // If this is the hovered rect
            if (rect === hoveredRect && !state.isHovering) {
              // Make it active
              state.isHovering = true;

              // Show the border
              gsap.to(state.border, {
                opacity: 1,
                duration: 0.2,
              });

              // Animate the border dash
              gsap.to(state.border, {
                strokeDashoffset: 20,
                repeat: -1,
                duration: 0.8,
                ease: "linear",
              });

              // Scale up the element slightly
              gsap.to(rect, {
                scale: 1.02,
                transformOrigin: "center",
                duration: 0.2,
                ease: "power2.out",
              });

              // Make cursor larger when over interactive element
              gsap.to(mouseShadowElement, {
                scale: 1.5,
                duration: 0.2,
              });
            }
            // If this rect was hovered but is no longer hovered
            else if (rect !== hoveredRect && state.isHovering) {
              // Make it inactive
              state.isHovering = false;

              // Hide the border
              gsap.to(state.border, {
                opacity: 0,
                duration: 0.2,
                onComplete: () => {
                  gsap.killTweensOf(state.border);
                },
              });

              // Scale back to normal
              gsap.to(rect, {
                scale: 1,
                duration: 0.2,
                ease: "power2.out",
              });

              // Return cursor to normal size only if no other rect is hovered
              let anyHovered = false;
              interactiveRects.forEach((r) => {
                const s = rectStates.get(r);
                if (r !== rect && s && s.isHovering) {
                  anyHovered = true;
                }
              });

              if (!anyHovered && hoveredRect === null) {
                gsap.to(mouseShadowElement, {
                  scale: 1.2,
                  duration: 0.2,
                });
              }
            }
          }
        });

        // Animate shadow and attached arrow to follow mouse with slight delay for smooth effect
        gsap.to(mouseShadowElement, {
          x: mouseX,
          y: mouseY,
          duration: 0.2,
          ease: "power2.out",
        });
      };

      // Initial position (bottom right corner)
      gsap.set(mouseShadowElement, {
        x: 370,
        y: 240,
        scale: 1,
        opacity: 0.85,
        transformOrigin: "0 0",
      });

      // Global hover state for the SVG
      let isHoveringInteractive = false;

      // Track when mouse enters the SVG container
      svg.addEventListener("mouseenter", () => {
        if (!isHoveringInteractive) {
          gsap.to(mouseShadowElement, {
            opacity: 1,
            scale: 1.2,
            duration: 0.3,
          });
        }
      });

      // Track when mouse leaves the SVG container
      svg.addEventListener("mouseleave", () => {
        // Reset all hover states when mouse leaves SVG
        interactiveRects.forEach((rect) => {
          const state = rectStates.get(rect);
          if (state && state.isHovering) {
            state.isHovering = false;

            // Hide the border
            gsap.to(state.border, {
              opacity: 0,
              duration: 0.2,
              onComplete: () => {
                gsap.killTweensOf(state.border);
              },
            });

            // Scale back to normal
            gsap.to(rect, {
              scale: 1,
              duration: 0.2,
              ease: "power2.out",
            });
          }
        });

        isHoveringInteractive = false;
        gsap.to(mouseShadowElement, {
          opacity: 0.85,
          scale: 1,
          x: 370, // Return to original position
          y: 240,
          duration: 0.6,
        });
      }); // Track mouse movement within SVG
      svg.addEventListener("mousemove", handleMouseMove);

      // Add moving border effect to interactive elements
      interactiveRects.forEach((rect) => {
        // Create a state object for this rect
        rectStates.set(rect, { isHovering: false, border: null });

        // Create a clone of the rect to use as a moving border
        const border = rect.cloneNode() as SVGRectElement;
        const parent = rect.parentNode;

        // Store the border in the state
        const rectState = rectStates.get(rect);
        if (rectState) {
          rectState.border = border;
        }

        // Set border attributes
        border.setAttribute("class", "hover-border");
        border.setAttribute("rx", rect.getAttribute("rx") || "0");
        border.setAttribute("ry", rect.getAttribute("ry") || "0");

        // Insert the border right after the original rect
        if (parent) {
          parent.insertBefore(border, rect.nextSibling);
        }

        // Hide the border initially
        gsap.set(border, {
          opacity: 0,
          strokeDasharray: "5,5",
          strokeDashoffset: 0,
        });

        // Create mouseenter handler
        const handleMouseEnter = () => {
          // Update state
          isHoveringInteractive = true;
          const state = rectStates.get(rect);
          if (state) {
            state.isHovering = true;
          }

          // Show the border
          gsap.to(border, {
            opacity: 1,
            duration: 0.3,
          });

          // Animate the border dash
          gsap.to(border, {
            strokeDashoffset: 20,
            repeat: -1,
            duration: 0.8,
            ease: "linear",
          });

          // Scale up the element slightly
          gsap.to(rect, {
            scale: 1.02,
            transformOrigin: "center",
            duration: 0.3,
            ease: "power2.out",
          });

          // Make cursor larger
          gsap.to(mouseShadowElement, {
            scale: 1.5,
            duration: 0.3,
          });
        };

        // Create mouseleave handler
        const handleMouseLeave = () => {
          // Update state
          isHoveringInteractive = false;
          const state = rectStates.get(rect);
          if (state) {
            state.isHovering = false;
          }

          // Hide the border
          gsap.to(border, {
            opacity: 0,
            duration: 0.3,
            onComplete: () => {
              gsap.killTweensOf(border);
            },
          });

          // Scale back to normal
          gsap.to(rect, {
            scale: 1,
            duration: 0.3,
            ease: "power2.out",
          });

          // Check if we're hovering over any other interactive elements
          let stillHovering = false;
          interactiveRects.forEach((r) => {
            const s = rectStates.get(r);
            if (s && s.isHovering) {
              stillHovering = true;
            }
          });

          // Only reset cursor if not hovering over any interactive element
          if (!stillHovering) {
            gsap.to(mouseShadowElement, {
              scale: 1.2,
              duration: 0.3,
            });
          }
        };

        // Add event listeners with reference to handlers for proper cleanup
        rect.addEventListener("mouseenter", handleMouseEnter);
        rect.addEventListener("mouseleave", handleMouseLeave);

        // Store handlers for cleanup
        if (rectState) {
          rectState.enterHandler = handleMouseEnter;
          rectState.leaveHandler = handleMouseLeave;
        }
      });

      // Register animation for cleanup
      registerAnimation("mouseShadow", mouseAnimation);

      // Cleanup function
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        // Remove SVG event listeners
        svg.removeEventListener("mouseenter", () => {});
        svg.removeEventListener("mouseleave", () => {});
        svg.removeEventListener("mousemove", handleMouseMove);

        // Clean up hover borders and event listeners for each rect
        interactiveRects.forEach((rect) => {
          // Get state for this rect
          const state = rectStates.get(rect);
          if (state) {
            // Remove event listeners using stored handlers
            if (state.enterHandler)
              rect.removeEventListener("mouseenter", state.enterHandler);
            if (state.leaveHandler)
              rect.removeEventListener("mouseleave", state.leaveHandler);

            // Remove the border element
            if (state.border && state.border.parentNode) {
              state.border.parentNode.removeChild(state.border);
            }

            // Kill any animations on this rect
            gsap.killTweensOf(rect);
            if (state.border) {
              gsap.killTweensOf(state.border);
            }
          }
        });

        // Kill any animations on the mouse shadow
        gsap.killTweensOf(mouseShadowElement);

        // Clear the rect states map
        rectStates.clear();
      };
    }
  }, [gsap, registerAnimation]);

  return (
    <section ref={sectionRef} className="section pt-[30px] lg:pt-[50px] pb-[50px] lg:pb-[70px] relative">
      <div className="container">
        <h2 className="sr-only">{t("sectionTitle")}</h2>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-[30px]"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {/* GRID ITEM 1 */}
          <div className="grid__item col-span-1 order-1 sticky top-[20px] self-start z-10 md:static md:top-auto md:z-auto lg:order-1 lg:col-span-2 lg:row-span-3">
            <motion.div variants={itemVariants}>
              <AnimatedCard
                className="w-full h-full rounded-[18px] lg:rounded-[25px]"
                circleClassName="w-[150px] h-[150px] blur-[90px]"
                disableInView={isMobile}
              >
                <BorderWrapper
                  wrapperClass="rounded-[18px] lg:rounded-[25px] h-full"
                  className="rounded-[18px] lg:rounded-[25px] min-h-[460px] lg:min-h-0 h-full bg-gradient-to-b from-[#121124] to-[#0e0821] pl-[30px] lg:pl-[40px] space-y-[30px] pt-[40px] lg:pt-[50px] pb-[30px] lg:pb-[40px] overflow-hidden"
                >
                <svg
                  ref={svgRef}
                  data-name="Layer 1"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  viewBox="0 0 418 292"
                  className="w-full h-auto mb-[32px] lg:mb-[60px] relative cursor-none"
                >
                  <defs>
                    <style>
                      {`.cls-1 {
                        fill: #8752fa;
                      }
                      .cls-2 {
                        fill: #51ff12;
                      }
                      .cls-3 {
                        fill: url(#${linearGradient2Id});
                      }
                      .cls-4 {
                        fill: url(#${linearGradientId});
                      }
                      .cls-5 {
                        fill: #ff267d;
                      }
                      .cls-6 {
                        fill: #fff704;
                      }
                      .cls-7 {
                        fill: none;
                        stroke: #3d3860;
                      }
                      .mouse-shadow {
                        fill: #8752fa;
                        opacity: 0.85;
                        pointer-events: none;
                      }
                      .cursor-arrow {
                        fill: #8752fa;
                        opacity: 0.9;
                        pointer-events: none;
                      }
                      @media (max-width: 1023px) {
                        .mouse-shadow {
                          display: none;
                        }
                      }
                      .hover-border {
                        stroke: #8752fa;
                        stroke-width: 1.5;
                        fill: none;
                        pointer-events: none;
                      }`}
                    </style>
                    <filter
                      id={glowId}
                      x="-100%"
                      y="-100%"
                      width="300%"
                      height="300%"
                      filterUnits="objectBoundingBox"
                    >
                      <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
                    </filter>
                    <linearGradient
                      id={linearGradientId}
                      x1="74.37"
                      y1="223"
                      x2="74.37"
                      y2="-4.37"
                      gradientTransform="translate(0 272) scale(1 -1)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0" stopColor="#3c1c63" />
                      <stop offset="1" stopColor="#3c1c63" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                      id={linearGradient2Id}
                      x1="265.24"
                      x2="265.24"
                      xlinkHref={`#${linearGradientId}`}
                    />
                  </defs>
                  <path
                    className="cls-7"
                    d="M10,.5h407.5v291H10c-5.25,0-9.5-4.25-9.5-9.5V10C.5,4.75,4.75.5,10,.5Z"
                  />
                  <line className="cls-7" x1="1" y1="32.5" x2="418" y2="32.5" />
                  <circle className="cls-5" cx="19.61" cy="16.61" r="3.61" />
                  <circle className="cls-6" cx="32" cy="16.61" r="3.61" />
                  <circle className="cls-2" cx="44.39" cy="16.61" r="3.61" />
                  <g>
                    <circle className="cls-5" cx="19.61" cy="16.61" r="3.61" />
                    <circle className="cls-6" cx="32" cy="16.61" r="3.61" />
                    <circle className="cls-2" cx="44.39" cy="16.61" r="3.61" />
                  </g>
                  <rect
                    className="cls-4"
                    x="28"
                    y="49"
                    width="92.74"
                    height="227.37"
                    rx="11.97"
                    ry="11.97"
                  />
                  <rect
                    className="cls-3"
                    x="139.89"
                    y="49"
                    width="250.7"
                    height="227.37"
                    rx="11.97"
                    ry="11.97"
                  />

                  {/* Mouse pointer group */}
                  <g ref={mouseShadowRef}>
                    {/* Arrow cursor */}
                    <path
                      className="cursor-arrow"
                      d="M0,0c-.56-1.6.97-3.13,2.57-2.57l18.24,6.97c1.79.63,1.79,3.17,0,3.8l-6.57,2.36c-1.92.67-3.43,2.18-4.11,4.11l-2.36,6.57c-.63,1.79-3.16,1.8-3.8,0L0,0Z"
                    />
                  </g>

                  {/* Original pointer position reference removed - no longer needed */}
                </svg>
                <div className="pr-[30px] lg:pr-[40px]">
                  <h3 className="text-[24px] lg:text-[28px] xl:text-[32px] font-semibold leading-[1.2] mb-[15px] break-words hyphens-auto">
                    {t("customSoftware.title")}
                  </h3>
                  <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                    {t("customSoftware.description")}
                  </p>
                </div>
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </div>
          {/* GRID ITEM 2 */}
          <div className="grid__item col-span-1 order-2 sticky top-[56px] self-start z-20 md:static md:top-auto md:z-auto lg:order-2 lg:col-span-3 lg:row-span-2">
            <motion.div
              variants={itemVariants}
              onMouseEnter={() => setUiHovered(true)}
              onMouseLeave={() => setUiHovered(false)}
            >
              <AnimatedCard
                className="w-full h-full rounded-[18px] lg:rounded-[25px]"
                circleClassName="w-[150px] h-[150px] blur-[90px]"
              >
                <BorderWrapper
                  wrapperClass="rounded-[18px] lg:rounded-[25px] h-full"
                  className="rounded-[18px] lg:rounded-[25px] h-full min-h-[460px] lg:min-h-[321px] grid lg:grid-cols-[1fr_240px] gap-[30px] bg-gradient-to-b from-[#121124] to-[#0e0821] pl-[30px] lg:pl-[40px] space-y-[30px] pt-[40px] lg:pt-[50px] ps-[30px] pb-[30px] lg:pb-[40px] pr-[30px] lg:pr-[40px] overflow-hidden"
                >
                <div className="grid__item lg:order-2 relative">
                  {/* CONTENT IS THERE */}
                  <ul className="space-y-[20px] lg:space-y-[30px] w-full flex flex-col items-end">
                    {[
                      { label: t("uiux.pill1"),  offset: "-translate-x-[84px] lg:-translate-x-[50px] xl:-translate-x-[104px]", delay: 0 },
                      { label: t("uiux.pill2"), offset: "-translate-x-[44px] lg:-translate-x-[26px] xl:-translate-x-[57px]",  delay: 0.35 },
                      { label: t("uiux.pill3"),   offset: "-translate-x-[0px]",                          delay: 0.7 },
                    ].map(({ label, offset, delay }) => (
                      <li key={label} className={`inline-block ${offset} relative before:content-[""] before:absolute before:top-2/4 before:w-full before:h-[1px] before:bg-[#FFEDED]/10 before:z-[-1] before:start-full before:-translate-y-2/4`}>
                        <motion.div
                          className="inline-flex items-start gap-[10px] bg-[#1d1a32] rounded-[24px] lg:rounded-[30px] p-[12px] lg:p-[14px] min-w-[175px] lg:min-w-[230px] border"
                          animate={{
                            borderColor: uiHovered ? "rgba(255,255,255,0.5)" : "rgba(255,237,237,0.1)",
                            boxShadow: uiHovered ? "0 0 16px rgba(255,255,255,0.08)" : "0 0 0px rgba(0,0,0,0)",
                          }}
                          transition={{ duration: 0.25, ease: "easeOut", delay }}
                        >
                          {/* Checkmark — bounces in and turns solid white */}
                          <motion.span
                            className="flex items-center justify-center text-white"
                            style={{ opacity: 0.25 }}
                            animate={
                              uiHovered
                                ? { scale: [1, 1.6, 0.85, 1], opacity: 1 }
                                : { scale: 1, opacity: 0.25 }
                            }
                            transition={{ duration: 0.45, ease: "easeOut", delay }}
                          >
                            <Icons.check className="w-[19px] h-[20px] lg:w-[25px] lg:h-[25px]" />
                          </motion.span>
                          {/* Label — fades to solid white */}
                          <motion.div
                            className="text-[18px] lg:text-[24px] font-semibold leading-[1]"
                            style={{ opacity: 0.25 }}
                            animate={{ opacity: uiHovered ? 1 : 0.25 }}
                            transition={{ duration: 0.3, ease: "easeOut", delay }}
                          >
                            {label}
                          </motion.div>
                        </motion.div>
                      </li>
                    ))}
                  </ul>

                  {/* SHAPE 1 */}
                  <div
                    className="absolute -top-[4vw] -start-[8vw] w-[240px] h-[240px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.3)_0%,_rgba(98,42,173,0.3)_39.42%,_rgba(34,12,81,0)_100%)] blur-[50px] rotate-90 animate-shape-pulse"
                    />
                </div>
                <div className="grid__item lg:order-1">
                  <div className="flex flex-col justify-end h-full">
                    <h3 className="text-[24px] lg:text-[28px] xl:text-[32px] font-semibold leading-[1.2] mb-[15px] break-words hyphens-auto">
                      {t("uiux.title")}
                    </h3>
                    <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                      {t("uiux.description")}
                    </p>
                  </div>
                </div>
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </div>
          {/* GRID ITEM 3 */}
          <motion.div variants={itemVariants} className="grid__item col-span-1 order-3 row-span-1 lg:order-3 lg:block hidden">
            <div className="flex items-center justify-center h-full lg:min-h-[280px]">
              <Image
                src="/assets/illustrations/flyingcode-icon.svg"
                alt="Flyingcode icon"
                width={184}
                height={118}
                className="w-full flex justify-center items-center h-auto"
              />
            </div>
          </motion.div>
          {/* GRID ITEM 4 */}
          <div className="grid__item col-span-1 order-5 sticky top-[42px] self-start z-40 md:static md:top-auto md:z-auto lg:order-4 lg:col-span-2 lg:row-span-2">
            <motion.div
              variants={itemVariants}
              onMouseEnter={() => setCloudHovered(true)}
              onMouseLeave={() => setCloudHovered(false)}
            >
              <AnimatedCard
                className="w-full h-full rounded-[18px] lg:rounded-[25px]"
                circleClassName="w-[150px] h-[150px] blur-[90px]"
              >
                <BorderWrapper
                  wrapperClass="rounded-[18px] lg:rounded-[25px] h-full"
                  className="rounded-[18px] lg:rounded-[25px] h-full min-h-[460px] lg:min-h-0 bg-[linear-gradient(180deg,_#121124_0%,_rgba(18,_17,_36,_0.5)_100%)] pl-[30px] lg:pl-[40px] space-y-[30px] pt-[40px] lg:pt-[50px] pb-[30px] lg:pb-[40px] bg-blend-multiply overflow-hidden"
                >
                <div className="h-[220px] lg:h-[320px] mb-[32px] lg:mb-[60px] relative">
                  <Image
                    src="/assets/illustrations/platforms.svg"
                    alt="E-commerce and platform integrations"
                    width={418}
                    height={292}
                    className="w-full h-auto absolute bottom-0 -end-[4rem] mix-blend-plus-lighter flex items-end justify-end"
                  />

                  {/* Light beam overlay — same viewBox as features-4.svg */}
                  <motion.svg
                    viewBox="0 0 424 453"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-full h-auto absolute bottom-0 -end-[4rem] pointer-events-none"
                    style={{ opacity: 0 }}
                    animate={{ opacity: cloudHovered ? 1 : 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <defs>
                      <filter id={beamGlowId} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2.5" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                      </filter>
                    </defs>

                    {[
                      { d: "M45.295 184.722V201.115C45.295 206.133 49.363 210.2 54.38 210.2H140.603",        delay: 0 },
                      { d: "M45.295 263.988V247.596C45.295 242.578 49.363 238.51 54.38 238.51H140.603",      delay: 0.25 },
                      { d: "M379.343 184.722V201.115C379.343 206.133 375.275 210.2 370.257 210.2H284.035",   delay: 0.1 },
                      { d: "M379.343 263.988V247.596C379.343 242.578 375.275 238.51 370.257 238.51H284.035", delay: 0.35 },
                      { d: "M212.307 362.513V298.006",                                                        delay: 0.5 },
                      { d: "M212.307 89.038V153.546",                                                         delay: 0.5 },
                    ].map(({ d, delay }, i) => (
                      <g key={i} filter={`url(#${beamGlowId})`}>
                        {/* Wide soft glow */}
                        <motion.path
                          d={d}
                          stroke="#c4b5fd"
                          strokeWidth="5"
                          strokeLinecap="round"
                          strokeOpacity="0.25"
                          strokeDasharray="8 55"
                          animate={{ strokeDashoffset: [100, -20] }}
                          transition={{ duration: 1.1, repeat: Infinity, delay, ease: "linear", repeatDelay: 0 }}
                        />
                        {/* Thin bright core */}
                        <motion.path
                          d={d}
                          stroke="#ede9fe"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeDasharray="8 55"
                          animate={{ strokeDashoffset: [100, -20] }}
                          transition={{ duration: 1.1, repeat: Infinity, delay, ease: "linear", repeatDelay: 0 }}
                        />
                      </g>
                    ))}
                  </motion.svg>

                  {/* SHAPE 2 */}
                  <div
                    className="absolute top-0 -end-[4rem] w-[249.85px] h-[248.94px] bg-[linear-gradient(180deg,_#9680C5_0%,_#622AAD_39.42%,_rgba(34,12,81,0)_100%)] blur-[45px] rotate-90 animate-shape-pulse"
                    />
                </div>
                <div className="pr-[40px]">
                  <h3 className="text-[24px] lg:text-[28px] xl:text-[32px] font-semibold leading-[1.2] mb-[15px] break-words hyphens-auto">
                    {t("ecommerce.title")}
                  </h3>
                  <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                    {t("ecommerce.description")}
                  </p>
                </div>
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </div>
          {/* GRID ITEM 5 */}
          <div className="grid__item col-span-1 order-4 sticky top-[92px] self-start z-30 md:static md:top-auto md:z-auto lg:order-5 lg:col-span-3 lg:row-span-1">
            <motion.div
              variants={itemVariants}
              onMouseEnter={() => setWebHovered(true)}
              onMouseLeave={() => setWebHovered(false)}
            >
              <AnimatedCard
                className="w-full h-full rounded-[18px] lg:rounded-[25px]"
                circleClassName="w-[150px] h-[150px] blur-[90px]"
              >
                <BorderWrapper
                  wrapperClass="rounded-[18px] lg:rounded-[25px] h-full"
                  className="rounded-[18px] lg:rounded-[25px] h-full min-h-[460px] lg:min-h-[321px] items-end grid lg:grid-cols-[1fr_240px] gap-x-[30px] gap-y-[20px] bg-gradient-to-b from-[#121124] to-[#0e0821] pl-[30px] lg:pl-[40px] pt-[40px] lg:pt-[50px] ps-[30px] pr-[30px] overflow-hidden lg:pr-[40px]"
                >
                <div className="grid__item">
                  <div className="pb-[40px]">
                    <h3 className="text-[24px] lg:text-[28px] xl:text-[32px] font-semibold leading-[1.2] mb-[15px] break-words hyphens-auto">
                      {t("mobileApps.title")}
                    </h3>
                    <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                      {t("mobileApps.description")}
                    </p>
                  </div>
                </div>
                <div className="grid__item flex items-end overflow-hidden">
                  <motion.div
                    className="w-full relative"
                    animate={{ y: webHovered ? -12 : 0, scale: webHovered ? 1.05 : 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    style={{ transformOrigin: "bottom center" }}
                  >
                    <Image
                      src="/assets/illustrations/mobile-application.svg"
                      alt="Mobile app interface"
                      width={243}
                      height={281}
                      className="w-full h-auto"
                    />

                    {/* Animated code icon overlay — same viewBox as features-5.svg */}
                    <svg
                      viewBox="0 0 243 281"
                      className="absolute inset-0 w-full h-full pointer-events-none"
                    >
                      {/* < symbol */}
                      <motion.path
                        d="M38 106.059L45.2465 113.386H53.4106L46.1641 106.059L53.4106 98.7263H45.2465L38 106.059Z"
                        fill="white"
                        initial={{ scale: 0, opacity: 0 }}
                        style={{ transformBox: "fill-box", transformOrigin: "center" }}
                        animate={webHovered ? { scale: [0, 1.4, 1], opacity: [0, 1, 1] } : { scale: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut", delay: 0.35 }}
                      />
                      {/* / symbol */}
                      <motion.path
                        d="M53.4111 98.7263L64.7345 110.184L53.4111 121.641H76.5897L65.2663 110.184L76.5897 98.7263L65.0004 87L53.4111 98.7263Z"
                        fill="white"
                        initial={{ scale: 0, opacity: 0 }}
                        style={{ transformBox: "fill-box", transformOrigin: "center" }}
                        animate={webHovered ? { scale: [0, 1.4, 1], opacity: [0, 1, 1] } : { scale: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut", delay: 0.52 }}
                      />
                      {/* > symbol */}
                      <motion.path
                        d="M84.7478 98.7263H76.5889L83.8354 106.059L76.5889 113.386H84.7478L91.9995 106.059L84.7478 98.7263Z"
                        fill="white"
                        initial={{ scale: 0, opacity: 0 }}
                        style={{ transformBox: "fill-box", transformOrigin: "center" }}
                        animate={webHovered ? { scale: [0, 1.4, 1], opacity: [0, 1, 1] } : { scale: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut", delay: 0.69 }}
                      />
                    </svg>
                  </motion.div>
                </div>
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
